import { ApolloLink, Operation } from "apollo-link";
import { BatchHttpLink } from "apollo-link-batch-http";
import { setContext } from "apollo-link-context";
import { ErrorResponse, onError } from "apollo-link-error";
import { RetryLink } from "apollo-link-retry";
import { createUploadLink } from "apollo-upload-client";
import jwtDecode from "jwt-decode";
import { getStoredIdToken, reauthenticate } from "lib/auth";
import { isAppReviewer } from "../../../shared/isAppReviewer";
import { makeApiRequestHeaders } from "../api";
import { AuthTokenError } from "./errors";
import { PrismicLink } from "./prismicLink";
export { default as OperationCounterLink } from "./operationCounterLink";

// NEXT_PUBLIC_API_PATH is optional in order to reduce size of environment variables defined in AWS Lambda, as they have
// a limit of 4KB. It is always `/graphql` in dev & prod, but locally when running Lambda via `serverless offline`, we
// need to set it to something else.
// An if condition is used over short-circuit assignment (A = A || B) because when the application is bundled by
// webpack, `process.env.*` are interpolated at build time, meaning if NEXT_PUBLIC_API_PATH is set in environment
// variables as defined by DefinePlugin, we would end up with something like `"/graphql" = "/graphql" || false`,
// which is invalid.
if (!process.env.NEXT_PUBLIC_API_PATH) {
  process.env.NEXT_PUBLIC_API_PATH = "/graphql";
}

const shouldReauthenticate = (errors) => {
  const codes = [
    // Logged into auth0 but no account with that address
    "NO_USER",
    // Invalid token
    "UNAUTHENTICATED",
  ];
  return errors?.some((e) => codes.includes(e.extensions.code));
};

export const createRetryLink = () =>
  new RetryLink({
    attempts: {
      retryIf: (error: Error, _operation: Operation) =>
        !!error && //default
        !(error instanceof AuthTokenError) &&
        !("noRetry" in _operation.getContext()),
    },
  });

export const createErrorLink = (ctx) =>
  onError((error: ErrorResponse) => {
    const { operation, graphQLErrors, networkError } = error;
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) =>
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        )
      );

      if (shouldReauthenticate(graphQLErrors)) {
        reauthenticate(ctx);
      }
    }

    // no additional logging required for AuthTokenError
    if (networkError instanceof AuthTokenError) {
      return;
    }

    if (networkError) {
      console.error("graphql network error:");
      console.error(operation);
      console.error(networkError);
    }
  });

export const createPrismicLink = () =>
  PrismicLink({
    uri: "https://penfold.prismic.io/graphql",
    accessToken: process.env.NEXT_PUBLIC_PRISMIC_ACCESS_TOKEN,
  });

const createBatchLink = () =>
  new BatchHttpLink({
    // Similar to the default key serialiser, but allows `batchKey` in context
    // This allows us to prevent seccl queries from slowing down faster batches
    // See: https://github.com/apollographql/apollo-client/blob/main/src/link/batch-http/batchHttpLink.ts#L192-L206
    batchKey: (operation: Operation) => {
      const context = operation.getContext();

      return JSON.stringify({
        http: context.http,
        options: context.fetchOptions,
        credentials: context.credentials,
        headers: context.headers,
        batchKey: context.batchKey,
        uri: context.uri ?? "/graphql",
      });
    },
  });

const getPenfoldUri = ({ token, newUserEmail }) => {
  const email = newUserEmail ?? (token && jwtDecode(token).email);
  const isReviewer = isAppReviewer(email);
  if (isReviewer) {
    return "https://api.getpenfold.dev/graphql";
  }
  return `${process.env.NEXT_PUBLIC_API_URL}${process.env.NEXT_PUBLIC_API_PATH}`;
};

export const createPenfoldLink = (ctx) => {
  const authLink = setContext(async (operation, context) => {
    const token = await getStoredIdToken(ctx);

    if (!token && !context.allowAnonymous) {
      console.warn(`No auth token: ${operation.operationName}`);
      throw new AuthTokenError(operation.operationName);
    }

    return {
      ...context,
      uri: getPenfoldUri({ token, newUserEmail: context.newUserEmail }),
      headers: makeApiRequestHeaders(token, context.headers),
    };
  });

  const httpLink = ApolloLink.split(
    (operation) => operation.getContext().hasUpload,
    createUploadLink(),
    createBatchLink()
  );

  return ApolloLink.from([authLink, httpLink]);
};
